import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from 'reactstrap';
import classnames from 'classnames';
import { useGetProductDetailsQuery } from '../../redux/productApiSlice';
import ProductProfileSection from './sections/ProductProfileSection';
import AuditProfileSection from './sections/AuditProfileSection';
import FeaturesTab from './tabs/FeaturesTab';
import ProductsTab from './tabs/ProductsTab';
import PricesTab from './tabs/PricesTab';
import StockTab from './tabs/StockTab';
import StockMatrixTab from './tabs/StockMatrixTab';
import { ConfirmationModal } from 'Components/Common/DeleteModal';
import { toast } from 'react-toastify';

export interface ProductConfiguratorFormData {
  // Product Profile fields
  productId: string;
  name: string;
  productCode: string;
  category: string;
  subcategory: string;
  vendor: string;
  vendorProductCode: string;
  brand: string;
  brandProductCode: string;
  subtype: string;
  description: string;
  longDescription: string;
  active: boolean;
  orderable: boolean;
  taxable: boolean;

  // Audit Profile fields (read-only)
  created: string;
  createdBy: string;
  lastUpdated: string;
  lastUpdatedBy: string;
  locationOwner: string;
}

interface ProductConfiguratorModalProps {
  isOpen: boolean;
  onClose: () => void;
  productData?: Partial<ProductConfiguratorFormData>;
  productRowId?: number;
}

const defaultFormData: ProductConfiguratorFormData = {
  productId: '',
  name: '',
  productCode: '',
  category: '',
  subcategory: '',
  vendor: '',
  vendorProductCode: '',
  brand: '',
  brandProductCode: '',
  subtype: '',
  description: '',
  longDescription: '',
  active: true,
  orderable: true,
  taxable: true,
  created: '',
  createdBy: '',
  lastUpdated: '',
  lastUpdatedBy: '',
  locationOwner: ''
};

const ProductConfiguratorModal: React.FC<ProductConfiguratorModalProps> = ({
  isOpen,
  onClose,
  productData = {},
  productRowId
}) => {
  // State for form data
  const [formData, setFormData] = useState<ProductConfiguratorFormData>({
    ...defaultFormData,
    ...productData
  });

  const [isPendingChanges, setIsPendingChanges] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  // State for active tab
  const [activeTab, setActiveTab] = useState<string>('features');

  // State for dirty tracking
  const [isDirty, setIsDirty] = useState(false);

  // API query - only fetch when productRowId is provided and modal is open
  const {
    data: searchResponse,
    isLoading: isSearchLoading,
    error: searchError,
    isSuccess: isSearchSuccess
  } = useGetProductDetailsQuery(
    {
      productRowId: productRowId || 0
    },
    {
      skip: !isOpen || !productRowId
    }
  );

  // Map API response to form data
  useEffect(() => {
    if (isSearchSuccess && searchResponse?.data && searchResponse.data.length > 0) {
      const productData = searchResponse.data[0];

      // Map product data to form data
      const mappedFormData: ProductConfiguratorFormData = {
        productId: productData.rowId?.toString() || productRowId?.toString() || '',
        name: productData.name || '',
        productCode: productData.productCode || '',
        category: productData.category || '',
        subcategory: '', // Not available in ProductGridData, might need separate API call
        vendor: productData.vendorName || '',
        vendorProductCode: '', // Not available in ProductGridData
        brand: '', // Not available in ProductGridData
        brandProductCode: '', // Not available in ProductGridData
        subtype: '', // Not available in ProductGridData
        description: '', // Not available in ProductGridData
        longDescription: '', // Not available in ProductGridData
        active: productData.activeFlag ?? true,
        orderable: true, // Not available in ProductGridData
        taxable: true, // Not available in ProductGridData
        // Audit fields from audit profile API
        created: productData?.created || '',
        createdBy: productData?.createdName || '',
        lastUpdated: productData?.updated || '',
        lastUpdatedBy: productData?.updatedName || '',
        locationOwner: productData?.ownerLocName || '',
      };

      setFormData(mappedFormData);
      setIsDirty(false);
    }
  }, [isSearchSuccess, searchResponse, productRowId]);

  // Handle search error
  useEffect(() => {
    if (searchError && isOpen && productRowId) {
      toast.error('Failed to load product data');
      console.error('Product search error:', searchError);
    }
  }, [searchError, isOpen, productRowId]);

  // Tab toggle handler
  const toggleTab = useCallback((tab: string) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  }, [activeTab]);

  // Form field change handler
  const handleFieldChange = useCallback((field: keyof ProductConfiguratorFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setIsDirty(true);
  }, []);

  // Action handlers
  const handleApplyTemplate = useCallback(() => {
    // TODO: Implement apply template functionality
    console.log('Apply Template clicked');
  }, []);

  const handleSaveAsTemplate = useCallback(() => {
    // TODO: Implement save as template functionality
    console.log('Save as Template clicked');
  }, []);

  const handleSwitchToAssociate = useCallback(() => {
    // TODO: Implement switch to associate functionality
    console.log('Switch to Associate clicked');
  }, []);

  const handleNew = useCallback(() => {
    setFormData(defaultFormData);
    setIsDirty(false);
  }, []);

  // Reset form data when modal closes or productRowId changes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        ...defaultFormData,
        ...productData
      });
      setIsDirty(false);
    }
  }, [isOpen, productData]);

  const handleSave = useCallback(() => {
    // TODO: Implement save functionality
    console.log('Save clicked', formData);
  }, [formData]);

  const handleCancel = useCallback(() => {
    if (isPendingChanges) {
      setShowConfirmation(true);
    } else {
      onClose();
    }
  }, [isPendingChanges, onClose]);

  const handleConfirmCancel = useCallback(() => {
    setIsPendingChanges(false);
    onClose();
  }, [onClose]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" centered className="product-configurator-modal modal-xl">
      <ModalHeader toggle={onClose} className="bg-light pb-2">
        <i className="ri-settings-3-line me-2"></i>
        Product Configurator
      </ModalHeader>

      <ModalBody className="p-0">
        {/* Loading indicator */}
        {isSearchLoading && productRowId && (
          <div className="text-center p-3">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <span className="ms-2">Loading product data...</span>
          </div>
        )}

        {/* Top Section - Product Profile and Audit Profile */}
        {(!isSearchLoading || !productRowId) && (
          <div className="border-bottom p-3">
            <Row className="g-3">
              {/* Product Profile Section */}
              <Col lg={6}>
                <ProductProfileSection
                  formData={formData}
                  onFieldChange={handleFieldChange}
                />
              </Col>

              {/* Audit Profile Section */}
              <Col lg={4}>
                <AuditProfileSection formData={formData} />
              </Col>
            </Row>
          </div>
        )}

        {/* Tabs Navigation */}
        <Nav tabs className="nav-tabs-custom arrow-navtabs nav-success mb-2">
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === 'features' })}
              onClick={() => toggleTab('features')}
              style={{ cursor: 'pointer' }}
            >
              Features
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === 'products' })}
              onClick={() => toggleTab('products')}
              style={{ cursor: 'pointer' }}
            >
              Products
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === 'prices' })}
              onClick={() => toggleTab('prices')}
              style={{ cursor: 'pointer' }}
            >
              Prices
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === 'stock' })}
              onClick={() => toggleTab('stock')}
              style={{ cursor: 'pointer' }}
            >
              Stock
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === 'stockMatrix' })}
              onClick={() => toggleTab('stockMatrix')}
              style={{ cursor: 'pointer' }}
            >
              Stock Matrix
            </NavLink>
          </NavItem>
        </Nav>

        {/* Tab Content */}
        <TabContent activeTab={activeTab} className="px-3">
          <TabPane tabId="features">
            <FeaturesTab setIsPendingChanges={setIsPendingChanges} productRowId={productRowId || 0} />
          </TabPane>
          <TabPane tabId="products">
            <ProductsTab />
          </TabPane>
          <TabPane tabId="prices">
            <PricesTab />
          </TabPane>
          <TabPane tabId="stock">
            <StockTab />
          </TabPane>
          <TabPane tabId="stockMatrix">
            <StockMatrixTab />
          </TabPane>
        </TabContent>
      </ModalBody>

      <ModalFooter className="bg-light mb-0 p-1">
        <button
          type="button"
          className="btn btn-soft-success btn-sm"
          style={{ minWidth: 80 }}
          onClick={handleNew}
        >
          New
        </button>
        <button
          type="button"
          className="btn btn-soft-primary btn-sm"
          style={{ minWidth: 80 }}
          onClick={handleSave}
          disabled={!isDirty}
        >
          Save
        </button>
        <button
          type="button"
          className="btn btn-soft-secondary btn-sm"
          style={{ minWidth: 80 }}
          onClick={handleCancel}
        >
          Cancel
        </button>
      </ModalFooter>

      <ConfirmationModal
        show={showConfirmation}
        onCloseClick={() => setShowConfirmation(false)}
        onDeleteClick={handleConfirmCancel}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to cancel and lose your changes?"
        deleteButtonText="Yes, Cancel"
        cancelButtonText="Keep Editing"
      />
    </Modal>
  );
};

export default ProductConfiguratorModal;
